import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useTwoFactorAction } from "@/shared/hooks/useTwoFactorAction";
import { readApiCode, readApiStatus } from "@/shared/utils/apiError";
import { mayHaveTakenEffect } from "@/shared/utils/unresolvedAttempt";

import { WalletDebitsService } from "../services/walletDebits.service";
import type { CreateWalletDebitPayload, WalletDebitCreated } from "../types/walletDebits.types";
import { buildDebitPayload } from "../utils/walletDebit";
import {
  isSameDebit,
  reconcileDebitAtSend,
  settleWalletDebitLookup,
  unresolvedWalletDebits,
  type WalletDebitAttempt,
  WalletDebitBusyError,
  type WalletDebitLookup,
  withWalletDebitLock,
} from "../utils/walletDebitAttempt";

export type WalletDebitInput = Omit<CreateWalletDebitPayload, "idempotency_key">;

/** `placed` carries the attempt it settled, which may not be the debit on the form. */
export type WalletDebitCheck =
  | { kind: "placed"; debit: WalletDebitCreated; attempt: CreateWalletDebitPayload }
  | { kind: WalletDebitLookup };

/** Refused client-side: an earlier debit to the organization is still unresolved and this one differs. */
export class UnresolvedWalletDebitError extends Error {
  constructor() {
    super("An earlier debit to this organization is still unresolved");
    this.name = "UnresolvedWalletDebitError";
  }
}

/**
 * The POST behind the TOTP, safe against a lost answer — the same contract as `useCreateWalletTopUp`, on its
 * own store and lock: the key follows the debit, the attempt is stored before the request leaves and removed
 * only once its outcome is known, a retry of the same debit reuses its key (the backend replays it instead of
 * debiting twice), and any other debit to the organization waits for `check`.
 */
export function useCreateWalletDebit(organizationId: string | null) {
  // The challenge closes as soon as the code leaves; the dialog shows its own progress.
  const twoFactor = useTwoFactorAction<WalletDebitCreated>({ closeOnSend: true });
  // The last body sent from this dialog with its key; null once it is known to have debited.
  const lastRef = useRef<CreateWalletDebitPayload | null>(null);
  // Bumped on every write to the store, so the attempt read below follows it.
  const [version, setVersion] = useState(0);
  const bump = useCallback(() => setVersion((value) => value + 1), []);

  // Another tab's write shows here at once: its unresolved debit blocks this one too.
  useEffect(() => unresolvedWalletDebits.subscribe(bump), [bump]);

  const unresolved: WalletDebitAttempt | null = useMemo(
    () => (organizationId ? unresolvedWalletDebits.get(organizationId) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `version` is the store's change signal
    [organizationId, version],
  );

  const { start } = twoFactor;
  const submit = useCallback(
    async (input: WalletDebitInput, currency: string | null): Promise<WalletDebitCreated | null> => {
      const organization = input.organization_id;
      const pending = unresolvedWalletDebits.get(organization);
      if (pending && !isSameDebit(pending.payload, input)) throw new UnresolvedWalletDebitError();

      const last = lastRef.current;
      const key =
        pending?.payload.idempotency_key ??
        (last && isSameDebit(last, input) ? last.idempotency_key : crypto.randomUUID());
      let payload = buildDebitPayload({ ...input, idempotency_key: key });
      lastRef.current = payload;
      // Unresolved before this submit (here or in another tab): only a success or `check` settles it, since a
      // refusal of this retry says nothing about the request whose answer was lost.
      let inherited = !!pending;
      let sent = false;
      try {
        const created = await start((code) =>
          withWalletDebitLock(organization, () => {
            const decision = reconcileDebitAtSend(unresolvedWalletDebits.get(organization), payload);
            if (!decision) throw new UnresolvedWalletDebitError();
            if (decision.adopted) {
              payload = decision.payload;
              lastRef.current = payload;
              inherited = true;
            }
            // Before the request leaves: a lost answer must find the key here, whatever happens to the dialog.
            unresolvedWalletDebits.set(organization, { payload, currency, sent_at: Date.now() });
            sent = true;
            return WalletDebitsService.create(payload, code);
          }),
        );
        if (created) {
          unresolvedWalletDebits.clear(organization, payload.idempotency_key);
          lastRef.current = null;
        } else if (sent && !inherited) {
          // Cancelled at the prompt: every code tried was refused before anything ran.
          unresolvedWalletDebits.clear(organization, payload.idempotency_key);
        }
        return created;
      } catch (error: unknown) {
        if (sent && !inherited && !mayHaveTakenEffect(readApiStatus(error))) {
          unresolvedWalletDebits.clear(organization, payload.idempotency_key);
        }
        throw error;
      } finally {
        bump();
      }
    },
    [start, bump],
  );

  /** Asks the backend what became of the unresolved attempt. No 2FA: it only reads. */
  const check = useCallback(async (): Promise<WalletDebitCheck> => {
    if (!organizationId) return { kind: "unknown" };
    const pending = unresolvedWalletDebits.get(organizationId);
    if (!pending) return { kind: "not_placed" };
    try {
      // Under the send lock: a send of this browser still on the wire settles first, so its key is never
      // looked up while its request may not have reached the server.
      const debit = await withWalletDebitLock(organizationId, () =>
        WalletDebitsService.findByKey(organizationId, pending.payload.idempotency_key),
      );
      unresolvedWalletDebits.clear(organizationId, pending.payload.idempotency_key);
      lastRef.current = null;
      // Found by its key: it was debited once, and this read debited nothing.
      return {
        kind: "placed",
        debit: { ...debit, replayed: true, emails: null },
        attempt: pending.payload,
      };
    } catch (error: unknown) {
      if (error instanceof WalletDebitBusyError) return { kind: "pending" };
      const outcome = settleWalletDebitLookup(
        readApiStatus(error),
        readApiCode(error),
        pending.sent_at ?? null,
      );
      if (outcome === "not_placed") {
        unresolvedWalletDebits.clear(organizationId, pending.payload.idempotency_key);
        // Typed again, the same debit keeps this key: even a request still on the wire could only debit once.
        lastRef.current = pending.payload;
      }
      return { kind: outcome };
    } finally {
      bump();
    }
  }, [organizationId, bump]);

  return { submit, check, unresolved, twoFactor };
}
